<template>
  <div class="p-2 h-full">
    <ComponentState
      :fetch-status="[buyMarketDepthStatus, sellMarketDepthStatus]"
    >
      <template #loading>
        <MarketDepthSkeleton />
      </template>

      <template #error>
        <div class="h-[calc(100%-1rem)] mb-4">
          <Error @retry="retryMarketDepth" />
        </div>
      </template>

      <MarketDepthTable
        v-if="buyMarketDepth.length || sellMarketDepth.length"
        :buy-market-depth="buyMarketDepth"
        :sell-market-depth="sellMarketDepth"
        :lower-price-threshold="symbol.lowerPriceThreshold ?? 0"
        :upper-price-threshold="symbol.upperPriceThreshold ?? 0"
        :isin="symbol.isin ?? ''"
        :is-option="Boolean(symbol?.instrumentAssetClassName)"
      />

      <EmptyList
        v-else
        :title="t('common.noInformationAvailable')"
        title-class="text-center mt-3"
        class="h-full flex flex-col justify-center items-center bg-neutral-primary rounded-md"
      >
        <template #image>
          <Image src="empty_box" alt="not_found" class="w-28 h-max" />
        </template>
      </EmptyList>
    </ComponentState>
  </div>
</template>

<script setup lang="ts">
import {
  useLiveBuyMarketDepthQuery,
  useLiveSellMarketDepthQuery,
} from '@core/symbol';
import { Instrument } from '@libs/api-reference';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { MarketDepthTable, MarketDepthSkeleton } from '@/modules/symbol';

import EmptyList from '@/components/EmptyList.vue';
import Error from '@/components/Error.vue';
import ComponentState from '@/components/ComponentState.vue';
import Image from '@/components/Image.vue';

type MarketDepthType = {
  symbol: Instrument;
};

const props = defineProps<MarketDepthType>();

const isin = computed(() => {
  return props.symbol.isin;
});

const exchangeCode = computed(() => {
  return props.symbol.exchangeCode;
});

const { t } = useI18n();

const {
  buyMarketDepth,
  isErrorBuyMarketDepth,
  getBuyMarketDepth,
  buyMarketDepthStatus,
} = useLiveBuyMarketDepthQuery({
  isin,
  exchangeCode,
});

const {
  sellMarketDepth,
  isErrorSellMarketDepth,
  getSellMarketDepth,
  sellMarketDepthStatus,
} = useLiveSellMarketDepthQuery({
  isin,
  exchangeCode,
});

const retryMarketDepth = () => {
  if (isErrorSellMarketDepth.value) {
    getSellMarketDepth();
  }

  if (isErrorBuyMarketDepth.value) {
    getBuyMarketDepth();
  }
};
</script>
